import React, { useState, useEffect } from 'react';
import { leaveAPI } from '../../api/leaveAPI';
import Spinner from '../spinner';
import './leaveDetailsSummary.css';

export default function LeaveDetailsSummary({
    leaveData,
    showBalanceInfo = true,
    compact = false
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [balanceInfo, setBalanceInfo] = useState({
        allocatedLeaves: 0,
        takenLeaves: 0,
        pendingLeaves: 0,
        partiallyApprovedLeaves: 0,
        balanceLeaves: 0
    });

    useEffect(() => {
        if (leaveData && showBalanceInfo && leaveData.employeeCode && leaveData.leaveTypeCode) {
            loadBalanceInfo();
        }
    }, [leaveData, showBalanceInfo]);

    const loadBalanceInfo = async () => {
        setIsLoading(true);
        try {
            const balanceData = await leaveAPI.getBalanceLeaveByLeaveType(
                leaveData.employeeCode,
                leaveData.leaveTypeCode,
                leaveData.pCode,
                leaveData.startDate
            );

            setBalanceInfo({
                allocatedLeaves: balanceData.allocatedLeaves,
                takenLeaves: balanceData.takenLeaves,
                pendingLeaves: balanceData.pendingLeaves,
                partiallyApprovedLeaves: balanceData.partiallyApprovedLeaves,
                balanceLeaves: balanceData.allocatedLeaves - (balanceData.takenLeaves + balanceData.pendingLeaves)
            });
        } catch (error) {
            console.error('Failed to load balance information:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const calculateLeaveDays = () => {
        if (!leaveData.startDate || !leaveData.endDate) return 0;
        const start = new Date(leaveData.startDate);
        const end = new Date(leaveData.endDate);
        return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'Approved': 'success',
            'Pending': 'warning',
            'Rejected': 'danger'
        };
        return statusMap[status] || 'secondary';
    };

    if (isLoading) {
        return (
            <div className="text-center py-3">
                <Spinner />
                <small className="text-muted">Loading details...</small>
            </div>
        );
    }

    return (
        <div className={`leave-details-summary ${compact ? 'compact' : ''}`}>
            <div className="row">
                {/* Basic Information */}
                <div className="col-lg-6 mb-3">
                    <div className="detail-section">
                        <h6 className="section-title">
                            <i className="fas fa-info-circle me-2"></i>
                            Leave Information
                        </h6>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <span className="detail-label">Duration:</span>
                                <span className="detail-value">
                                    <span className="badge bg-info">{calculateLeaveDays()} day(s)</span>
                                </span>
                            </div>
                            <div className="detail-item">
                                <span className="detail-label">Type:</span>
                                <span className="detail-value">
                                    {leaveData?.leaveTypeName}
                                </span>
                            </div>
                            <div className="detail-item">
                                <span className="detail-label">Period:</span>
                                <span className="detail-value">
                                    {formatDate(leaveData?.startDate)} - {formatDate(leaveData?.endDate)}
                                </span>
                            </div>
                            <div className="detail-item">
                                <span className="detail-label">Status:</span>
                                <span className="detail-value">
                                    <span className={`badge bg-${getStatusBadge(leaveData?.transType)}`}>
                                        {leaveData?.transType || 'Pending'}
                                    </span>
                                </span>
                            </div>
                            {leaveData?.isHalfDay && (
                                <div className="detail-item">
                                    <span className="detail-label">Day Type:</span>
                                    <span className="detail-value">
                                        <span className="badge bg-secondary">🕐 Half Day</span>
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Leave Balance Information */}
                {showBalanceInfo && (
                    <div className="col-lg-6 mb-3">
                        <div className="detail-section">
                            <h6 className="section-title">
                                <i className="fas fa-chart-pie me-2"></i>
                                Leave Balance
                            </h6>
                            <div className="balance-grid">
                                <div className="balance-item bg-primary">
                                    <div className="balance-number">{balanceInfo.allocatedLeaves}</div>
                                    <div className="balance-label">Allocated</div>
                                </div>
                                <div className="balance-item bg-success">
                                    <div className="balance-number">{balanceInfo.takenLeaves}</div>
                                    <div className="balance-label">Taken</div>
                                </div>
                                <div className="balance-item bg-warning">
                                    <div className="balance-number">{balanceInfo.pendingLeaves}</div>
                                    <div className="balance-label">Pending</div>
                                </div>
                                <div className="balance-item bg-info">
                                    <div className="balance-number">{balanceInfo.balanceLeaves}</div>
                                    <div className="balance-label">Remaining</div>
                                </div>
                            </div>                            
                        </div>
                    </div>
                )}
            </div>

            {/* Leave Reason */}
            {leaveData?.reason && (
                <div className="row">
                    <div className="col-12">
                        <div className="detail-section">
                            <h6 className="section-title">
                                <i className="fas fa-comment me-2"></i>
                                Leave Reason
                            </h6>
                            <div className="reason-display">
                                {leaveData.reason}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Approval Comments */}
            {leaveData?.approverComments && (
                <div className="row">
                    <div className="col-12">
                        <div className="detail-section">
                            <h6 className="section-title">
                                <i className="fas fa-clipboard-check me-2"></i>
                                Approver Comments
                            </h6>
                            <div className="reason-display approval-comment">
                                {leaveData.approverComments || 'No comments provided'}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
